import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSubscriptions } from "../../context/SubscriptionsContext";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";
import { UndoAction } from "../UndoAction";

export const FollowButton = ({ business, size = "md", className = "" }) => {
  const { user } = useAuth();
  const { isFollowing, toggleFollow, loaded } = useSubscriptions();
  const { toast } = useToast();
  const { t } = useLang();
  const navigate = useNavigate();
  const following = isFollowing(business);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({ title: t("follow.signInTitle"), description: t("follow.signInMsg") });
      navigate("/signup");
      return;
    }
    const nowFollowing = await toggleFollow(business);
    if (nowFollowing) {
      toast({ title: t("follow.now", { name: business }), description: t("follow.nowMsg") });
    } else {
      toast({
        title: t("follow.stopped", { name: business }),
        action: <UndoAction testid="undo-unfollow" onUndo={() => toggleFollow(business)} />,
      });
    }
  };

  const sz = size === "sm" ? "text-xs px-3 py-1.5" : "text-sm px-4 py-2";
  const style = following
    ? "bg-[#16a34a] text-white hover:bg-[#15803d] border border-[#16a34a]"
    : "bg-secondary text-foreground hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] border border-border";
  const iconSize = size === "sm" ? 13 : 15;

  if (user && !loaded) {
    return <span className={`inline-flex items-center gap-1.5 rounded-xl font-semibold ${sz} bg-secondary text-muted-foreground border border-border opacity-60`}><Bell size={iconSize} /> …</span>;
  }

  return (
    <button onClick={onClick} data-testid={`follow-btn-${business.replace(/\s+/g, "-").toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 rounded-xl font-semibold transition-colors ${sz} ${style} ${className}`}>
      {following ? <><BellRing size={iconSize} /> {t("follow.following")}</> : <><Bell size={iconSize} /> {t("follow.follow")}</>}
    </button>
  );
};

export default FollowButton;
